import { Component, input, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
    injectNgControl,
    NoopValueAccessorDirective,
} from '../../../directives/form/noop-value-accessor.directive';
import { Button } from '../../button/button.component';
import { Icon } from '../../icon/icon.component';
import { NgFor } from '@angular/common';

/** Ação opcional exibida à direita do campo de texto (ex.: botão com ícone). */
export type TextInputAction = {
    /** Identificador da ação. */
    id?: string | undefined | null;
    /** Classe do ícone (Font Awesome) exibido no botão da ação. */
    fontIcon?: string | null | undefined;
};

/**
 * Campo de texto integrável a formulários.
 *
 * Vincula-se ao `NgControl` do host (use com `formControl`/`ngModel`) por meio
 * da diretiva {@link NoopValueAccessorDirective}. Aceita rótulo/ícone projetados
 * (`ai-label`, `ai-icon`) e uma lista de {@link actions} exibidas como botões.
 */
@Component({
    selector: 'ai-text-input',
    templateUrl: 'text-input.component.html',
    styleUrls: ['text-input.component.scss'],
    hostDirectives: [NoopValueAccessorDirective],
    imports: [FormsModule, ReactiveFormsModule, Button, Icon, NgFor],
})
export class TextInput {
    /** Controle de formulário do host (injetado via NgControl). */
    readonly ngControl = injectNgControl();

    /** Ícone exibido junto ao campo. */
    readonly icon = input<string>('');

    /** Posição do ícone em relação ao campo. */
    readonly iconPlacement = input<'left' | 'right' | null>(null);

    /** Rótulo do campo. */
    readonly label = input<string>('');

    /** Texto de placeholder. */
    readonly placeholder = input<string>('');

    /** Ações exibidas à direita do campo. */
    readonly actions = input<TextInputAction[]>([]);

    /** Comprimento máximo permitido (atributo `maxlength`). */
    readonly maxLength = input<number | null | undefined>(undefined);

    /**
     * Exibe um contador `atual/máximo` (ex.: `21/50`) no canto direito do
     * campo, informando quantos caracteres já foram digitados em relação ao
     * limite. Requer {@link maxLength} definido.
     */
    readonly showCharacterCount = input<boolean>(false);

    /** Classe CSS extra para customizações específicas. */
    readonly cssClass = input<string>('');

    /** Emite a ação acionada pelo usuário. */
    actionPerformed = output<TextInputAction>();

    /** Comprimento atual do valor do controle (alimenta o contador). */
    protected get currentLength(): number {
        const value = this.ngControl.control?.value;
        return typeof value === 'string' ? value.length : 0;
    }

    /**
     * Indica se o campo está inválido e já foi interagido (sujo/tocado) — condição
     * para exibir o ícone de erro dentro do campo, à direita.
     */
    protected isInvalid(): boolean {
        const control = this.ngControl.control;
        return (
            !!control && control.invalid && (control.dirty || control.touched)
        );
    }

    /** Trata o clique em um botão de ação, emitindo {@link actionPerformed}. */
    onActionButtonClicked(event: Event, action: TextInputAction): void {
        this.actionPerformed.emit(action);
    }
}
